import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../utils/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { X, Send, Minus, UserPlus, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const MASCOT_SRC = '/operio-mascot-3d.png';
const STORAGE_KEY = 'operio_public_chat_session';

// Anonym chatt-widget för landningssida / Om oss / Kontakt.
// Kräver ingen auth. session_id sparas i sessionStorage så att samtalet överlever sidbyten i samma flik.
const PublicChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [view, setView] = useState('chat'); // 'chat' | 'human-form'
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);
  const [supportHours, setSupportHours] = useState(null);

  // Human-handover form
  const [humanForm, setHumanForm] = useState({ name: '', email: '', phone: '', note: '' });
  const [submittingHuman, setSubmittingHuman] = useState(false);
  const [humanRequested, setHumanRequested] = useState(false);

  const messagesEndRef = useRef(null);

  useEffect(() => {
    api.getSupportHours().then((r) => setSupportHours(r.data)).catch(() => {});
  }, []);

  const ensureSession = useCallback(async () => {
    let sid = sessionStorage.getItem(STORAGE_KEY);
    if (sid) {
      setSessionId(sid);
      try {
        const res = await api.publicChatMessages(sid);
        setMessages(res.data.messages || []);
        setHumanRequested(!!res.data.session?.human_requested);
        return sid;
      } catch {
        // session expirerad eller borta → ny
        sessionStorage.removeItem(STORAGE_KEY);
      }
    }
    setStarting(true);
    try {
      const res = await api.publicChatStart({});
      sid = res.data.session_id;
      sessionStorage.setItem(STORAGE_KEY, sid);
      setSessionId(sid);
      const initial = [res.data.greeting];
      if (res.data.follow_up) initial.push(res.data.follow_up);
      setMessages(initial);
      return sid;
    } catch (e) {
      console.error('Kunde inte starta chatten', e);
      toast.error('Kunde inte starta chatten. Försök igen.');
      return null;
    } finally {
      setStarting(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen && !sessionId) {
      ensureSession();
    }
  }, [isOpen, sessionId, ensureSession]);

  // Poll for new messages (e.g. support replies) while open
  useEffect(() => {
    if (!isOpen || !sessionId) return;
    const interval = setInterval(async () => {
      try {
        const res = await api.publicChatMessages(sessionId);
        const incoming = res.data.messages || [];
        setMessages((prev) => {
          // Merge: keep optimistic temps, replace by id if duplicate
          const knownIds = new Set(prev.filter((m) => !String(m.id).startsWith('tmp-')).map((m) => m.id));
          const newOnes = incoming.filter((m) => !knownIds.has(m.id));
          if (newOnes.length === 0) return prev;
          const merged = [...prev.filter((m) => String(m.id).startsWith('tmp-')), ...incoming];
          return merged;
        });
        setHumanRequested(!!res.data.session?.human_requested);
      } catch (e) {
        // silent — polling, ignore transient errors
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [isOpen, sessionId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    const text = inputMessage.trim();
    if (!text || loading) return;
    const sid = sessionId || (await ensureSession());
    if (!sid) return;

    setInputMessage('');
    const optimistic = {
      id: 'tmp-' + Date.now(),
      sender: 'user',
      sender_name: 'Du',
      message: text,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimistic]);
    setLoading(true);
    try {
      const res = await api.publicChatSend({ session_id: sid, message: text });
      setMessages((prev) => [
        ...prev.filter((m) => m.id !== optimistic.id),
        res.data.user_message,
        res.data.ai_response,
      ]);
    } catch (err) {
      toast.error('Kunde inte skicka. Försök igen.');
      setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
    } finally {
      setLoading(false);
    }
  };

  const submitHumanRequest = async (e) => {
    e.preventDefault();
    if (!humanForm.name.trim() || !humanForm.email.trim()) {
      toast.error('Fyll i namn och e-post');
      return;
    }
    setSubmittingHuman(true);
    try {
      const sid = sessionId || (await ensureSession());
      const res = await api.publicChatRequestHuman({ session_id: sid, ...humanForm });
      setMessages((prev) => [...prev, res.data.system_message]);
      setHumanRequested(true);
      setView('chat');
      const isOpenNow = res.data.support_hours?.is_open;
      toast.success(isOpenNow
        ? 'Tack! En medarbetare svarar inom kort.'
        : 'Tack! Vi hör av oss nästa öppettid.');
    } catch (e) {
      toast.error('Något gick fel. Försök igen.');
    } finally {
      setSubmittingHuman(false);
    }
  };

  // ===== Closed state — circular mascot head only
  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="group fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 flex flex-col items-end gap-2 focus:outline-none"
        data-testid="public-chat-open-btn"
        aria-label="Öppna chatt"
      >
        <span className="hidden sm:inline-block relative max-w-[230px] text-left bg-white text-[#1A1A1A] text-xs font-medium px-3.5 py-2 rounded-2xl rounded-br-sm shadow-lg border border-[#E8E2D9] animate-[bounce_3s_ease-in-out_infinite]">
          Hej! <span className="text-[#E8613A]">Fråga mig</span> om Operio
          <span className="absolute -bottom-1.5 right-5 w-3 h-3 bg-white border-r border-b border-[#E8E2D9] rotate-45" />
        </span>
        <span className="relative inline-block">
          <span className="absolute inset-0 rounded-full bg-[#E8613A]/30 blur-2xl scale-110 group-hover:scale-125 transition-transform duration-300" />
          <span className="relative inline-flex items-start justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full overflow-hidden bg-[#FFE9DD] ring-4 ring-white shadow-2xl transition-transform duration-200 group-hover:scale-105 group-hover:-rotate-3">
            <img
              src={MASCOT_SRC}
              alt="Operio maskot"
              className="w-full h-auto object-contain object-top scale-[3.2] translate-y-[34%] pointer-events-none select-none"
            />
          </span>
        </span>
      </button>
    );
  }

  const supportOpen = !!supportHours?.is_open;

  return (
    <div className="fixed bottom-3 right-3 sm:bottom-6 sm:right-6 z-50 flex flex-col items-end" data-testid="public-chat-widget">
      <img
        src={MASCOT_SRC}
        alt="Operio maskot"
        className={`relative z-10 drop-shadow-2xl pointer-events-none select-none transition-all duration-300 ${
          isMinimized ? 'w-16 sm:w-20 h-16 sm:h-20 -mb-3 sm:-mb-4 mr-2' : 'w-20 sm:w-28 h-20 sm:h-28 -mb-5 sm:-mb-6 mr-3'
        }`}
      />

      <div className={`relative bg-white rounded-2xl border border-[#E8E2D9] shadow-2xl overflow-hidden transition-all duration-200 ${
        isMinimized ? 'w-64 sm:w-72 h-12' : 'w-[calc(100vw-1.5rem)] sm:w-96 max-w-sm h-[78vh] sm:h-[560px] max-h-[640px]'
      }`}>
        {/* Header */}
        <div className="bg-[#1A1A1A] text-white px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <span className={`w-2 h-2 rounded-full ${supportOpen ? 'bg-green-400' : 'bg-amber-400'} animate-pulse`} />
            <div className="min-w-0">
              <p className="text-sm font-semibold leading-none">Operio Chat</p>
              <p className="text-[10px] text-white/60 mt-0.5 truncate">
                {supportOpen ? 'Kundtjänst öppen nu' : (supportHours?.hours_label || 'AI svarar 24/7')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setIsMinimized((m) => !m)}
              className="p-1 hover:bg-white/10 rounded transition-colors"
              data-testid="public-chat-minimize-btn"
              aria-label="Minimera"
            >
              <Minus size={14} />
            </button>
            <button
              onClick={() => { setIsOpen(false); setIsMinimized(false); }}
              className="p-1 hover:bg-white/10 rounded transition-colors"
              data-testid="public-chat-close-btn"
              aria-label="Stäng"
            >
              <X size={14} />
            </button>
          </div>
        </div>

        {!isMinimized && view === 'chat' && (
          <>
            {/* Messages */}
            <div className="h-[calc(100%-9.5rem)] overflow-y-auto px-4 py-3 space-y-3 bg-[#FAF7F2]" data-testid="public-chat-messages">
              {starting && messages.length === 0 && (
                <div className="text-center py-10">
                  <img src={MASCOT_SRC} alt="" className="w-12 h-12 mx-auto mb-2 opacity-80" />
                  <p className="text-xs text-[#7A7570]">Startar samtalet…</p>
                </div>
              )}
              {messages.map((m) => {
                const isUser = m.sender === 'user';
                const isSupport = m.sender === 'support';
                return (
                  <div key={m.id} className={`flex gap-2 ${isUser ? 'flex-row-reverse' : ''}`}>
                    {!isUser && (
                      isSupport ? (
                        <span className="w-7 h-7 flex-shrink-0 rounded-full bg-green-100 text-green-700 text-[10px] font-bold flex items-center justify-center mt-0.5">
                          {(m.sender_name || 'S').charAt(0)}
                        </span>
                      ) : (
                        <img src={MASCOT_SRC} alt="" className="w-7 h-7 flex-shrink-0 mt-0.5" />
                      )
                    )}
                    <div className="max-w-[78%]">
                      {isSupport && (
                        <p className="text-[10px] text-green-700 font-semibold mb-0.5 ml-1">{m.sender_name || 'Support'}</p>
                      )}
                      <div className={`px-3 py-2 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                        isUser
                          ? 'bg-[#E8613A] text-white rounded-br-sm'
                          : isSupport
                          ? 'bg-green-50 text-[#1A1A1A] border border-green-200 rounded-bl-sm'
                          : 'bg-white text-[#1A1A1A] border border-[#E8E2D9] rounded-bl-sm'
                      }`}>
                        {m.message}
                      </div>
                    </div>
                  </div>
                );
              })}
              {loading && (
                <div className="flex gap-2 items-end">
                  <img src={MASCOT_SRC} alt="" className="w-7 h-7 flex-shrink-0" />
                  <div className="bg-white border border-[#E8E2D9] px-3 py-2 rounded-2xl rounded-bl-sm">
                    <div className="flex gap-1">
                      <span className="w-1.5 h-1.5 bg-[#7A7570] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-1.5 h-1.5 bg-[#7A7570] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-1.5 h-1.5 bg-[#7A7570] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Action bar — request human */}
            <div className="absolute bottom-[3.5rem] left-0 right-0 px-3 py-2 border-t border-[#E8E2D9] bg-white flex items-center justify-between gap-2">
              <p className="text-[11px] text-[#7A7570] flex-1 truncate">
                {humanRequested
                  ? '✓ En människa hör av sig'
                  : (supportOpen ? 'Eller prata med en riktig person:' : `Kundtjänst öppen ${supportHours?.hours_label?.replace('Vardagar ', 'vard. ') || ''}`)}
              </p>
              <button
                onClick={() => setView('human-form')}
                disabled={humanRequested}
                className="text-[11px] font-semibold text-[#E8613A] hover:underline disabled:opacity-50 disabled:no-underline disabled:cursor-not-allowed flex items-center gap-1 whitespace-nowrap"
                data-testid="public-chat-request-human-btn"
              >
                <UserPlus size={12} /> Kontakta människa
              </button>
            </div>

            {/* Input */}
            <div className="absolute bottom-0 left-0 right-0 p-3 bg-white border-t border-[#E8E2D9]">
              <form onSubmit={handleSend} className="flex gap-2">
                <Input
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  placeholder="Skriv ditt meddelande..."
                  disabled={loading || starting}
                  className="flex-1 h-9 text-sm rounded-full"
                  data-testid="public-chat-input"
                />
                <Button
                  type="submit"
                  disabled={loading || starting || !inputMessage.trim()}
                  className="bg-[#E8613A] hover:bg-[#D4532F] text-white h-9 w-9 p-0 rounded-full"
                  data-testid="public-chat-send-btn"
                  aria-label="Skicka"
                >
                  <Send size={14} />
                </Button>
              </form>
            </div>
          </>
        )}

        {!isMinimized && view === 'human-form' && (
          <div className="h-[calc(100%-3.25rem)] overflow-y-auto px-4 py-4 bg-[#FAF7F2]">
            <button
              type="button"
              onClick={() => setView('chat')}
              className="text-xs text-[#7A7570] hover:text-[#1A1A1A] flex items-center gap-1 mb-3"
              data-testid="public-chat-back-btn"
            >
              <ArrowLeft size={12} /> Tillbaka till chatten
            </button>
            <h3 className="text-base font-bold text-[#1A1A1A]">Kontakta en riktig människa</h3>
            <p className="text-xs text-[#6B6560] mt-1">
              {supportOpen
                ? 'Kundtjänsten är öppen nu. Lämna dina uppgifter så hör vi av oss inom kort.'
                : `Kundtjänsten är stängd just nu (öppet ${supportHours?.hours_label || ''}). Vi hör av oss nästa öppettid.`}
            </p>
            <form onSubmit={submitHumanRequest} className="mt-4 space-y-3">
              <Input
                value={humanForm.name}
                onChange={(e) => setHumanForm({ ...humanForm, name: e.target.value })}
                placeholder="Namn *"
                className="h-10 text-sm"
                data-testid="public-chat-human-name"
                required
              />
              <Input
                type="email"
                value={humanForm.email}
                onChange={(e) => setHumanForm({ ...humanForm, email: e.target.value })}
                placeholder="E-post *"
                className="h-10 text-sm"
                data-testid="public-chat-human-email"
                required
              />
              <Input
                value={humanForm.phone}
                onChange={(e) => setHumanForm({ ...humanForm, phone: e.target.value })}
                placeholder="Telefon (valfritt)"
                className="h-10 text-sm"
                data-testid="public-chat-human-phone"
              />
              <textarea
                value={humanForm.note}
                onChange={(e) => setHumanForm({ ...humanForm, note: e.target.value })}
                placeholder="Kort om vad du vill (valfritt)"
                className="w-full text-sm rounded-md border border-input bg-background px-3 py-2 min-h-[80px]"
                data-testid="public-chat-human-note"
              />
              <Button
                type="submit"
                disabled={submittingHuman}
                className="w-full bg-[#E8613A] hover:bg-[#D4532F] text-white h-11 rounded-full font-semibold"
                data-testid="public-chat-human-submit"
              >
                {submittingHuman ? 'Skickar…' : 'Skicka förfrågan'}
              </Button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicChatWidget;
